import React from 'react';
import UITenantInfo from '@/components/shared/UITenantInfo';
import UIIconButton from '@/components/shared/UIIconButton';
import './UITenantTable.scss';

// Інлайн SVG компоненти для стрілок
const SortArrowUp: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="8"
    height="7"
    viewBox="0 0 8 7"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M3.03763 0.538462C3.46535 -0.179486 4.53465 -0.179488 4.96237 0.538461L7.84946 5.38461C8.27718 6.10256 7.74254 7 6.8871 7H1.1129C0.257467 7 -0.277182 6.10256 0.150536 5.38462L3.03763 0.538462Z"
      fill="#C4CDD5"
    />
  </svg>
);

const SortArrowDown: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="8"
    height="7"
    viewBox="0 0 8 7"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M3.03763 6.46154C3.46535 7.17949 4.53465 7.17949 4.96237 6.46154L7.84946 1.61539C8.27718 0.897437 7.74254 0 6.8871 0H1.1129C0.257467 0 -0.277182 0.897436 0.150536 1.61538L3.03763 6.46154Z"
      fill="#C4CDD5"
    />
  </svg>
);

export interface TenantData {
  id: number;
  name: string;
  email: string;
  avatar?: string | null; // Змінили тип на string | null
  unreadCount?: number; // Додаємо опціональне поле для непрочитаних
}

export interface UITenantTableProps {
  /**
   * Список тенантів
   */
  tenants?: TenantData[];

  /**
   * Обробник кліку на тенанта
   */
  onTenantClick?: (tenantId: number) => void;

  /**
   * Обробник кліку на кнопку інформації
   */
  onInfoClick?: (tenantId: number) => void;

  /**
   * Показувати заголовок таблиці
   */
  showHeader?: boolean;

  /**
   * Варіант відображення (compact для модального вікна)
   */
  variant?: 'default' | 'compact';

  /**
   * Показувати unread badges (для списку розмов)
   */
  showUnreadBadges?: boolean;

  /**
   * Додатковий CSS клас
   */
  className?: string;
}

const baseClass = 'UITenantTable';

const UITenantTable: React.FC<UITenantTableProps> = ({
  tenants = [],
  onTenantClick = (id) => console.log('Tenant clicked:', id),
  onInfoClick = (id) => console.log('Info clicked:', id),
  showHeader = true,
  variant = 'default',
  showUnreadBadges = false,
  className = '',
}) => {
  const containerClasses = [baseClass, `${baseClass}--${variant}`, className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerClasses}>
      {/* Заголовок таблиці */}
      {showHeader && (
        <div className={`${baseClass}__header`}>
          <span className={`${baseClass}__header-title`}>TENANT</span>
          <div className={`${baseClass}__header-sort`}>
            <SortArrowUp
              className={`${baseClass}__sort-arrow ${baseClass}__sort-arrow--up`}
            />
            <SortArrowDown
              className={`${baseClass}__sort-arrow ${baseClass}__sort-arrow--down`}
            />
          </div>
        </div>
      )}

      {/* Рядки тенантів */}
      <div className={`${baseClass}__body`}>
        {tenants.map((tenant) => (
          <div
            key={tenant.id}
            className={`${baseClass}__row`}
            onClick={() => onTenantClick(tenant.id)}
          >
            {/* Інформація про тенанта */}
            <UITenantInfo
              name={tenant.name}
              email={tenant.email}
              tenantId={tenant.id}
              avatar={tenant.avatar}
              size="medium"
              onClick={() => onTenantClick(tenant.id)}
            />

            {/* Badge з непрочитаними повідомленнями (тільки для розмов) */}
            {showUnreadBadges &&
              tenant.unreadCount !== undefined &&
              tenant.unreadCount > 0 && (
                <div className={`${baseClass}__unread-badge`}>
                  {tenant.unreadCount}
                </div>
              )}

            {/* Дії */}
            <div className={`${baseClass}__actions`}>
              <UIIconButton
                iconType="contact"
                variant="success"
                size="medium"
                onClick={(e) => {
                  e.stopPropagation();
                  onTenantClick(tenant.id);
                }}
              />
              <UIIconButton
                iconType="userinfo"
                variant="info"
                size="medium"
                onClick={(e) => {
                  e.stopPropagation();
                  onInfoClick(tenant.id);
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UITenantTable;
